import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useRegistration, useRegistrationDispatch } from "../context/RegistrationContext";
import * as UCJApi from "../constants/apiMethodsUCJ";

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS  = 120000; // 2 min max

const STATUS_LABELS = {
  NEW:         "Submission received",
  PCUST:       "Processing account",
  PCFUL:       "Provisioning entitlement",
  PEVAL:       "Processing evaluation request",
  PEFUL:       "Fulfilling evaluation",
  RERUN:       "Re-processing",
  ERROR:       "An error occurred",
  COMPL:       "Complete! Redirecting to AppHub...",
  INITIALIZED: "Initializing",
};

export default function StepProgress() {
  const state    = useRegistration();
  const dispatch = useRegistrationDispatch();

  const [statusLabel, setStatusLabel]   = useState("Submitting registration...");
  const [isError, setIsError]           = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const pollRef     = useRef(null);
  const startedRef  = useRef(false);

  // ── Submit registration on mount ─────────────────────────────────────────
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    submitRegistration();
    return () => clearInterval(pollRef.current);
  }, []);

  const submitRegistration = async () => {
    dispatch({ type: "SUBMIT_START" });
    try {
      const payload = buildPayload(state);
      const res = await axios.post(UCJApi.UCJ_REGISTER(), payload);
      const registrationId = (res.data || {}).registrationId;
      if (!registrationId) throw new Error("No registrationId in response");

      dispatch({ type: "SUBMIT_SUCCESS", payload: { registrationId } });
      setStatusLabel("Registration submitted. Waiting for provisioning...");
      startPolling(registrationId);
    } catch (err) {
      setIsError(true);
      setErrorMessage((err.response && err.response.data) || err.message || "Submission failed");
      dispatch({ type: "SUBMIT_ERROR", payload: err.message });
    }
  };

  const startPolling = (registrationId) => {
    const deadline = Date.now() + POLL_TIMEOUT_MS;

    pollRef.current = setInterval(async () => {
      if (Date.now() > deadline) {
        clearInterval(pollRef.current);
        setIsError(true);
        setErrorMessage("Timed out waiting for provisioning. Please check your email for confirmation.");
        return;
      }

      try {
        const res = await axios.get(UCJApi.UCJ_STATUS(registrationId));
        const { statusCode, appHubUrl } = res.data;

        setStatusLabel(STATUS_LABELS[statusCode] || `Processing (${statusCode})...`);

        if (statusCode === "COMPL") {
          clearInterval(pollRef.current);
          dispatch({ type: "SET_APPHUB_URL", payload: appHubUrl });
          if (appHubUrl) {
            setTimeout(() => window.location.assign(appHubUrl), 1500);
          }
        } else if (statusCode === "ERROR") {
          clearInterval(pollRef.current);
          setIsError(true);
          setErrorMessage("An error occurred during provisioning. Our team has been notified.");
        }
      } catch (err) {
        // transient poll failure — keep trying
        console.warn("Poll error:", err.message);
      }
    }, POLL_INTERVAL_MS);
  };

  return (
    <div className="ucj-progress-wrapper">
      <div className="ucj-progress-card">
        <img src={require("../images/logo.png")} alt="NVIDIA" className="ucj-logo" />

        {isError ? (
          <>
            <div className="ucj-progress-icon ucj-progress-icon--error">✗</div>
            <h2 className="ucj-progress-title">Something went wrong</h2>
            <p className="ucj-progress-message">{errorMessage}</p>
            <p className="ucj-progress-hint">
              If this problem persists, please contact NVIDIA support.
            </p>
          </>
        ) : (
          <>
            <div className="ucj-progress-spinner" />
            <h2 className="ucj-progress-title">Setting up your access</h2>
            <p className="ucj-progress-message">{statusLabel}</p>
            <p className="ucj-progress-hint">
              This usually takes less than 30 seconds. Please do not close this window.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// ── Payload builder ──────────────────────────────────────────────────────────

function buildPayload(state) {
  const { form, tenant, productFamily, licenseType, pakId, partner, ngcProductFamily } = state;
  return {
    firstName:            form.firstName,
    lastName:             form.lastName,
    emailID:              form.email,
    phone:                form.phone,
    jobRoleId:            form.jobRoleId,
    companyName:          form.companyName,
    countryId:            form.countryId,
    stateId:              form.stateId,
    city:                 form.city,
    zipCode:              form.zipCode,
    street1:              form.addressLine1,
    street2:              form.addressLine2,
    industryId:           form.industryId,
    claimingEntitlementAs: form.claimingEntitlementAs,
    partnerReferral:      form.partnerReferral,
    productFamily,
    licenseType,
    pakId,
    partner,
    ngcProductFamily,
    // Tenant/NCA fields
    nca_nan:          tenant.nca_nan,
    nca_nid:          tenant.nca_nid,
    starfleet_id:     tenant.starfleet_id,
    idp_id:           tenant.idp_id,
    accountName:      tenant.accountName,
    primaryOwnerEmail: tenant.primaryOwnerEmail,
    showPicker:       tenant.showPicker,
    ngcProductNames:  tenant.ngcProductNames,
    sessionId:        tenant.sessionId,
  };
}
